import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { actions, money, useRateCard, STUDIO } from "../lib/store";
import { computeEstimate, defaultConfig, PHASE_OUTLINE, PHASE_DETAIL, engagedDisciplines, scopedLines, type EstimateConfig, type EstimatePhase } from "../lib/rates";
import { totalArea, unitLabel } from "../lib/program";
import { useCan } from "../lib/auth";
import { Meter, Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { gsap } from "../lib/anim";
import { fmtDate } from "../lib/format";
import { openWhatsApp } from "../lib/whatsapp";
import type { Project, Deliverable } from "../lib/types";

// Map an estimate discipline key → the discipline label used on drawing deliverables.
const DELIV_DISCIPLINE: Record<string, string> = { architecture: "Architecture", interior: "Interiors", structural: "Structural", mep: "MEP", landscape: "Landscape" };
// Map a fee stage key → the deliverable phase it produces.
const STAGE_PHASE: Record<string, string> = { sd: "Conceptual", dd: "Design Development", cd: "IFT" };

const PHASE_COLOR: Record<string, string> = { sd: "var(--sky)", dd: "var(--sage)", cd: "var(--accent)", tender: "var(--gold)", ca: "var(--ink-3)" };
const phCol = (k: string) => PHASE_COLOR[k] ?? "var(--ink-3)";

export default function QuotationDoc({ p, mode = "studio" }: { p: Project; mode?: "studio" | "client" }) {
  const rc = useRateCard();
  const nav = useNavigate();
  // A client never holds the payments capability, so the construction-fee basis
  // and its comparison stay studio-side regardless of how this is rendered.
  const canEdit = useCan("payments") && mode === "studio";
  const config = p.estimate ?? defaultConfig(p.budget, p.currency);
  const est = computeEstimate(config, rc);
  const ccy = config.currency;
  const [disc, setDisc] = useState<string | null>(null);
  const [stage, setStage] = useState<string | null>(null);
  const deliverables = p.deliverables ?? [];
  const spaces = p.brief.spaces ?? [];
  const unit = p.brief.areaUnit ?? "sqft";

  const disciplineLines = est.lines.filter((l) => l.kind === "discipline");
  const addonLines = est.lines.filter((l) => l.kind === "addon");
  const delivsFor = (key: string) => deliverables.filter((d) => d.discipline === DELIV_DISCIPLINE[key]);
  const delivsInPhase = (stageKey: string) => deliverables.filter((d) => d.phase === STAGE_PHASE[stageKey]);

  return (
    <div className="reveal">
      {/* header */}
      <div className="card pad" style={{ marginBottom: 18 }}>
        <div className="row between wrap" style={{ gap: 16 }}>
          <div>
            <div className="kicker">Fee proposal · {p.code}</div>
            <h3 className="serif" style={{ fontSize: 26, marginTop: 6 }}>Design Services — {p.name}</h3>
            <div className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>{p.client.company} · {p.type} · {p.area || "—"}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="kicker">Total fee</div>
            <div className="serif" style={{ fontSize: 32, color: "var(--accent-ink)" }}>{money(est.total, ccy)}</div>
            {est.discount > 0 && (
              <div style={{ fontSize: 12.5, marginTop: 2 }}>
                <span className="dim" style={{ textDecoration: "line-through" }}>{money(est.gross, ccy)}</span>{" "}
                <span className="badge accent" style={{ fontSize: 10 }}>{est.discountPct}% discount · −{money(est.discount, ccy)}</span>
              </div>
            )}
            <div className="dim" style={{ fontSize: 12.5 }}>{(est.pctOfBudget * 100).toFixed(1)}% of {money(config.budget, ccy)} budget</div>

          </div>
        </div>
        {mode === "studio" && (
          <div className="row wrap" style={{ gap: 10, marginTop: 16 }}>
            <button className="btn ghost sm" style={{ color: "#25D366", borderColor: "#25D366" }} onClick={() => openWhatsApp(`${STUDIO.name} — fee proposal for ${p.name}: total ${money(est.total, ccy)} (${(est.pctOfBudget * 100).toFixed(1)}% of budget). Review in your portal: https://${STUDIO.domain}/portal/${p.id}`)}><Icon.whatsapp width={15} />Share</button>
            {canEdit && <button className="btn accent sm" onClick={() => { actions.saveQuoteFromEstimate(p.id, "Studio"); }}><Icon.check width={14} />Save as quotation</button>}
            {canEdit && <button className="btn ghost sm" onClick={() => nav(`/studio/${p.id}/estimator`)}><Icon.sliders width={14} />Adjust scope</button>}
          </div>
        )}
      </div>

      {/* fee by discipline */}
      <div className="card" style={{ overflow: "hidden", marginBottom: 18 }}>
        <div className="row between" style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)" }}>
          <div className="kicker">Fee by discipline</div><div className="kicker">% of budget · % of fee · amount</div>
        </div>
        {disciplineLines.map((l) => {
          const open = disc === l.key;
          const pctBudget = config.budget > 0 ? l.amount / config.budget * 100 : 0;
          const pctFee = est.gross > 0 ? l.amount / est.gross * 100 : 0;
          const dv = delivsFor(l.key);
          return (
            <div key={l.key} style={{ borderBottom: "1px solid var(--line)" }}>
              <button className="row between" onClick={() => setDisc(open ? null : l.key)} style={{ width: "100%", padding: "13px 20px", background: open ? "var(--paper-2)" : "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
                <span className="row" style={{ gap: 10 }}><Icon.caretDown width={15} style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .2s", color: "var(--ink-3)" }} /><span style={{ fontWeight: 600 }}>{l.label}</span></span>
                <span className="row" style={{ gap: 18 }}>
                  <span className="mono dim" style={{ fontSize: 12.5, width: 54, textAlign: "right" }}>{pctBudget.toFixed(1)}%</span>
                  <span className="badge accent" style={{ minWidth: 52, justifyContent: "center" }}>{pctFee.toFixed(0)}%</span>
                  <span className="mono" style={{ fontSize: 14, width: 90, textAlign: "right" }}>{money(l.amount, ccy)}</span>
                </span>
              </button>
              {open && (
                <div style={{ padding: "4px 20px 18px 45px" }}>
                  <div className="dim" style={{ fontSize: 12.5, marginBottom: 10 }}>{l.detail} · staged across the design process:</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8, marginBottom: 10 }}>
                    {rc.phases.filter((ph) => !(est.constructionIsFixed && ph.construction)).map((ph) => (
                      <div key={ph.key} className="panel" style={{ padding: "9px 11px" }}>
                        <div style={{ fontSize: 11.5, fontWeight: 500 }}>{ph.label}</div>
                        <div className="row between" style={{ marginTop: 3 }}><span className="dim mono" style={{ fontSize: 10.5 }}>{Math.round(ph.pct * 100)}%</span><span className="mono" style={{ fontSize: 12 }}>{money(Math.round(l.amount * ph.pct), ccy)}</span></div>
                      </div>
                    ))}
                  </div>
                  {est.constructionIsFixed && <div className="dim" style={{ fontSize: 11.5, marginBottom: 14 }}>Construction administration is charged as a flat fee across all disciplines, so it isn't staged per discipline here.</div>}
                  {dv.length > 0 && (
                    <>
                      <div className="kicker" style={{ fontSize: 9.5, marginBottom: 6 }}>Drawings to be produced ({dv.length})</div>
                      <div className="row wrap" style={{ gap: 6 }}>
                        {dv.map((d) => <span key={d.id} className="badge" style={{ fontSize: 10.5 }}><span className="mono">{d.code}</span> {d.title}</span>)}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {addonLines.map((l) => (
          <div key={l.key} className="row between" style={{ padding: "12px 20px 12px 45px", borderBottom: "1px solid var(--line)" }}>
            <span style={{ fontSize: 14 }}>{l.label} <span className="dim" style={{ fontSize: 12 }}>· {l.detail}</span></span>
            <span className="mono" style={{ fontSize: 14 }}>{money(l.amount, ccy)}</span>
          </div>
        ))}
        {est.discount > 0 && (
          <>
            <div className="row between" style={{ padding: "12px 20px", borderBottom: "1px solid var(--line)" }}>
              <span>Full fee before discount</span><span className="mono">{money(est.gross, ccy)}</span>
            </div>
            <div className="row between" style={{ padding: "12px 20px", color: "var(--accent-ink)" }}>
              <span>Discount ({est.discountPct}%)</span><span className="mono">−{money(est.discount, ccy)}</span>
            </div>
          </>
        )}
        <div className="row between" style={{ padding: "16px 20px", background: "var(--paper-2)", fontWeight: 600 }}>
          <span>Total professional fee</span><span className="serif" style={{ fontSize: 20 }}>{money(est.total, ccy)}</span>
        </div>
      </div>

      {/* design process — distribution + outline per stage */}
      <div className="card pad" style={{ marginBottom: 18 }}>
        <div className="row between" style={{ marginBottom: 14 }}>
          <div className="kicker">Design process — fee distribution by stage</div>
          <span className="mono dim" style={{ fontSize: 12 }}>{money(est.total, ccy)} total</span>
        </div>

        {/* distribution bar */}
        <div style={{ display: "flex", height: 12, borderRadius: 8, overflow: "hidden", background: "var(--line)" }}>
          {est.phases.map((ph) => <div key={ph.key} title={`${ph.label} · ${ph.fixed ? "fixed fee" : `${ph.pctLabel}%`}`} style={{ flex: Math.max(0.001, ph.pct), background: phCol(ph.key), display: "grid", placeItems: "center" }}>
            {ph.pct >= 0.1 && <span style={{ fontSize: 9, color: "#fff", fontWeight: 600 }} className="mono">{ph.pctLabel}%</span>}
          </div>)}
        </div>
        <div className="row wrap" style={{ gap: 14, marginTop: 10, marginBottom: 18 }}>
          {est.phases.map((ph) => <span key={ph.key} className="row" style={{ gap: 6, fontSize: 11 }}><span style={{ width: 9, height: 9, borderRadius: 3, background: phCol(ph.key) }} /><span className="dim">{ph.label}</span></span>)}
        </div>

        {/* stage cards — each opens a detail lightbox */}
        <div className="col" style={{ gap: 12 }}>
          {est.phases.map((ph, i) => {
            const dv = delivsInPhase(ph.key);
            return (
              <button key={ph.key} className="panel stage-btn" onClick={() => setStage(ph.key)}
                style={{ padding: 16, borderLeft: `3px solid ${phCol(ph.key)}`, textAlign: "left", cursor: "pointer", width: "100%", display: "block" }}>
                <div className="row between wrap" style={{ gap: 10 }}>
                  <div className="row" style={{ gap: 12 }}>
                    <span className="serif" style={{ fontSize: 20, color: "var(--ink-3)", lineHeight: 1, width: 26 }}>{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="row wrap" style={{ gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{ph.label}</span>
                        {ph.construction && <span className="badge accent" style={{ fontSize: 9 }}>during construction</span>}
                        {ph.fixed && <span className="badge" style={{ fontSize: 9 }}>fixed fee</span>}
                      </div>
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>Cumulative to {money(ph.cumulative, ccy)}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="serif" style={{ fontSize: 20 }}>{money(ph.amount, ccy)}</div>
                    <span className="badge accent" style={{ fontSize: 10 }}>{ph.fixed ? "fixed" : `${ph.pctLabel}% of fee`}</span>
                  </div>
                </div>
                {PHASE_OUTLINE[ph.key] && <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 12 }}>{PHASE_OUTLINE[ph.key]}</div>}
                <div className="row between" style={{ marginTop: 12, gap: 10 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>{dv.length > 0 ? `${dv.length} drawing${dv.length > 1 ? "s" : ""} in this stage` : "Stage detail"}</span>
                  <span className="stage-cta row" style={{ gap: 5, fontSize: 11.5, color: "var(--accent-ink)", fontWeight: 600 }}>View stage detail<Icon.arrowUpRight width={12} /></span>
                </div>
              </button>
            );
          })}
        </div>

        {/* construction fee basis — % of fee vs a flat fee. Studio only. */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
          <div className="row between wrap" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 13 }}>Paid during construction (CA)</span>
            <span className="mono" style={{ fontWeight: 600 }}>{money(est.constructionAmount, ccy)}{est.constructionIsFixed && <span className="dim" style={{ fontWeight: 400 }}> · fixed</span>}</span>
          </div>
          {canEdit && <ConstructionBasis p={p} config={config} est={est} ccy={ccy} />}
        </div>
      </div>

      {/* scope — spaces */}
      {spaces.length > 0 && (
        <div className="card pad" style={{ marginBottom: 18 }}>
          <div className="row between" style={{ marginBottom: 12 }}><div className="kicker">Scope of works — accommodation</div><span className="dim" style={{ fontSize: 13 }}>{totalArea(spaces).toLocaleString()} {unitLabel(unit)} · {spaces.length} spaces</span></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 6 }}>
            {spaces.map((s) => (
              <div key={s.id} className="row between" style={{ fontSize: 13, padding: "3px 0" }}>
                <span>{s.qty > 1 && <span className="mono dim">{s.qty}× </span>}{s.name}</span>
                <span className="mono dim">{(s.qty * s.area).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* additional services — out of scope, billed as-and-when */}
      {(rc.services ?? []).length > 0 && (
        <div className="card" style={{ overflow: "hidden", marginBottom: 18 }}>
          <div className="row between wrap" style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)", gap: 8 }}>
            <div className="kicker">Additional services &amp; charges</div>
            <span className="dim" style={{ fontSize: 12 }}>Outside the agreed scope · not included in the fee above</span>
          </div>
          {(rc.services ?? []).map((s, i) => (
            <div key={s.key} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 14, padding: "12px 20px", borderTop: i ? "1px solid var(--line)" : "none", alignItems: "baseline" }}>
              <div>
                <span style={{ fontWeight: 500, fontSize: 14 }}>{s.label}</span>
                {s.blurb && <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>{s.blurb}{s.included ? ` · first ${s.included} included in the fee` : ""}</div>}
              </div>
              <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                <span className="mono" style={{ fontSize: 14 }}>{money(s.rate, ccy)}</span>
                <span className="dim mono" style={{ fontSize: 12 }}> / {s.unit}</span>
              </div>
            </div>
          ))}
          <div className="dim" style={{ fontSize: 11.5, padding: "10px 20px", background: "var(--paper-2)", borderTop: "1px solid var(--line)" }}>
            Charged only when instructed. A written estimate is provided before any additional service is undertaken.
          </div>
        </div>
      )}

      <p className="dim" style={{ fontSize: 12, textAlign: "center", marginBottom: 8 }}>
        {STUDIO.name} · {fmtDate(new Date().toISOString().slice(0, 10))} · Fees exclude VAT and reimbursable expenses. Valid 30 days.
      </p>

      <StageLightbox
        phase={est.phases.find((x) => x.key === stage) ?? null}
        index={est.phases.findIndex((x) => x.key === stage)}
        deliverables={stage ? delivsInPhase(stage) : []}
        ccy={ccy}
        engaged={engagedDisciplines(p)}
        onClose={() => setStage(null)}
      />
      <style>{`
        .stage-btn { transition: transform .3s var(--ease-out), box-shadow .3s var(--ease-out), border-color .2s ease; }
        .stage-btn:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg); border-color: var(--line-2); }
        .stage-btn:active { transform: translateY(-1px) scale(.995); }
        .stage-btn .stage-cta { opacity: .5; transition: opacity .25s ease, gap .25s ease; }
        .stage-btn:hover .stage-cta { opacity: 1; gap: 9px; }
        @media (max-width: 720px){ .basis-grid{ grid-template-columns: 1fr !important } .sl-grid{ grid-template-columns: 1fr !important } }
        @media (prefers-reduced-motion: reduce){
          .stage-btn, .stage-btn .stage-cta { transition: none }
          .stage-btn:hover { transform: none }
        }
      `}</style>
    </div>
  );
}

/* ---------------- stage detail lightbox ---------------- */
const DELIV_STATUS: Record<string, { label: string; cls: string }> = {
  todo: { label: "To do", cls: "badge" },
  doing: { label: "In progress", cls: "badge info" },
  issued: { label: "Issued", cls: "badge ok" },
};
const KIND_ORDER = ["drawing", "schedule", "document", "visual"] as const;
const KIND_LABEL: Record<string, [string, string]> = {
  drawing: ["drawing", "drawings"], schedule: ["schedule", "schedules"],
  document: ["document", "documents"], visual: ["visual", "visuals"],
};
const KIND_TAG: Record<string, string> = { schedule: "SCH", document: "DOC", visual: "3D" };

/** One row of the drawing register — expands to what the sheet actually carries. */
function SheetRow({ d, first }: { d: Deliverable; first: boolean }) {
  const [open, setOpen] = useState(false);
  const st = DELIV_STATUS[d.status] ?? DELIV_STATUS.todo;
  const meta = [d.scale, d.sheet].filter(Boolean).join(" · ");
  const hasContents = !!d.contents?.length;
  const tag = KIND_TAG[d.kind ?? "drawing"];

  return (
    <div style={{ borderTop: first ? "none" : "1px solid var(--line)" }}>
      <button onClick={() => hasContents && setOpen(!open)} disabled={!hasContents}
        style={{ width: "100%", display: "block", textAlign: "left", background: open ? "var(--paper-2)" : "transparent", border: "none", padding: "9px 12px", cursor: hasContents ? "pointer" : "default" }}>
        <div className="row between" style={{ gap: 10 }}>
          <span className="row" style={{ gap: 9, minWidth: 0 }}>
            {hasContents
              ? <Icon.caretDown width={12} style={{ flexShrink: 0, color: "var(--ink-3)", transform: open ? "none" : "rotate(-90deg)", transition: "transform .2s" }} />
              : <span style={{ width: 12, flexShrink: 0 }} />}
            <span className="mono dim" style={{ fontSize: 11.5, width: 56, flexShrink: 0 }}>{d.code}</span>
            <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</span>
            {tag && <span className="badge" style={{ fontSize: 8.5, flexShrink: 0, padding: "1px 5px" }}>{tag}</span>}
          </span>
          <span className="row" style={{ gap: 10, flexShrink: 0 }}>
            {meta && <span className="mono dim" style={{ fontSize: 10.5 }}>{meta}</span>}
            {d.revision && <span className="mono dim" style={{ fontSize: 10.5 }}>{d.revision}</span>}
            <span className={st.cls} style={{ fontSize: 9.5 }}>{st.label}</span>
          </span>
        </div>
      </button>
      {open && hasContents && (
        <ul style={{ margin: 0, padding: "0 12px 11px 78px", listStyle: "none" }}>
          {d.contents!.map((c) => (
            <li key={c} className="row" style={{ gap: 7, alignItems: "flex-start", fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5, marginTop: 3 }}>
              <span style={{ color: "var(--ink-3)", flexShrink: 0 }}>—</span>{c}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StageLightbox({ phase, index, deliverables, ccy, engaged, onClose }: {
  phase: EstimatePhase | null; index: number; deliverables: Deliverable[]; ccy: string; engaged: Set<string> | null; onClose: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const open = !!phase;
  const key = phase?.key;
  useEffect(() => {
    if (!open) return;
    const ctx = gsap.context(() => {
      gsap.from(".sl-el", { y: 14, opacity: 0, duration: 0.45, ease: "power3.out", stagger: 0.06 });
    }, root);
    return () => ctx.revert();
  }, [open, key]);
  if (!phase) return null;

  const d = PHASE_DETAIL[phase.key];
  /* Only promise what the client actually appointed us for. */
  const activities = d ? scopedLines(d.activities, engaged) : [];
  const outputs = d ? scopedLines(d.outputs, engaged) : [];
  const byDiscipline = deliverables.reduce<Record<string, Deliverable[]>>((acc, x) => { (acc[x.discipline] ||= []).push(x); return acc; }, {});
  const byKind = deliverables.reduce<Record<string, number>>((acc, x) => { const k = x.kind ?? "drawing"; acc[k] = (acc[k] ?? 0) + 1; return acc; }, {});

  return (
    <Modal open={open} onClose={onClose} wide>
      <div ref={root}>
        <div className="sl-el row between wrap" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 14 }}>
            <span className="serif" style={{ fontSize: 34, color: phCol(phase.key), lineHeight: 1 }}>{String(index + 1).padStart(2, "0")}</span>
            <div>
              <h3 className="serif" style={{ fontSize: 25, lineHeight: 1.15 }}>{phase.label}</h3>
              <div className="row wrap" style={{ gap: 6, marginTop: 5 }}>
                {phase.construction && <span className="badge accent" style={{ fontSize: 9 }}>during construction</span>}
                {phase.fixed && <span className="badge" style={{ fontSize: 9 }}>fixed fee</span>}
                <span className="dim" style={{ fontSize: 11.5 }}>Cumulative to {money(phase.cumulative, ccy)}</span>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="serif" style={{ fontSize: 26 }}>{money(phase.amount, ccy)}</div>
            <span className="badge accent" style={{ fontSize: 10 }}>{phase.fixed ? "fixed" : `${phase.pctLabel}% of fee`}</span>
          </div>
        </div>

        <div className="divider" style={{ margin: "16px 0" }} />

        {d && (
          <>
            <p className="sl-el muted" style={{ fontSize: 14.5, lineHeight: 1.65 }}>{d.purpose}</p>
            <div className="sl-el sl-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, marginTop: 18 }}>
              <div>
                <div className="kicker" style={{ marginBottom: 8 }}>What we do</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {activities.map((x) => <li key={x} style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 4 }}>{x}</li>)}
                </ul>
              </div>
              <div>
                <div className="kicker" style={{ marginBottom: 8 }}>What you receive</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {outputs.map((x) => <li key={x} style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 4 }}>{x}</li>)}
                </ul>
              </div>
            </div>
            <div className="sl-el panel" style={{ padding: "12px 14px", marginTop: 16, borderLeft: `3px solid ${phCol(phase.key)}` }}>
              <div className="kicker" style={{ fontSize: 9.5, marginBottom: 3 }}>Your role at this stage</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{d.client}</div>
            </div>
          </>
        )}

        <div className="sl-el" style={{ marginTop: 22 }}>
          <div className="row between wrap" style={{ marginBottom: 10, gap: 8 }}>
            <div className="kicker">Deliverables issued in this stage</div>
            {deliverables.length > 0 && (
              <span className="row wrap dim" style={{ gap: 8, fontSize: 11.5 }}>
                {KIND_ORDER.filter((k) => byKind[k]).map((k) => <span key={k}>{byKind[k]} {KIND_LABEL[k][byKind[k] === 1 ? 0 : 1]}</span>)}
              </span>
            )}
          </div>
          {deliverables.length === 0 ? (
            <div className="panel dim" style={{ padding: "12px 14px", fontSize: 13, lineHeight: 1.55 }}>
              No new drawings are produced at this stage — it works from the Issued-for-Tender set delivered at Construction Documents.
            </div>
          ) : (
            <div className="col" style={{ gap: 14 }}>
              {Object.entries(byDiscipline).map(([disc, items]) => (
                <div key={disc}>
                  <div className="row between" style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>{disc}</span>
                    <span className="dim mono" style={{ fontSize: 11 }}>{items.length} sheet{items.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="card" style={{ overflow: "hidden" }}>
                    {items.map((x, i) => <SheetRow key={x.id} d={x} first={i === 0} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ---------------- construction fee basis (studio only) ---------------- */
function ConstructionBasis({ p, config, est, ccy }: {
  p: Project; config: EstimateConfig; est: ReturnType<typeof computeEstimate>; ccy: string;
}) {
  const fixedOn = est.constructionIsFixed;
  const set = (v: number | undefined) => actions.setEstimate(p.id, { ...config, constructionFixed: v });
  const pctBasisTotal = est.total - est.constructionDelta;
  const delta = est.constructionDelta;
  const pctShare = pctBasisTotal > 0 ? Math.round((est.constructionPctAmount / pctBasisTotal) * 100) : 0;
  const deltaColor = delta > 0 ? "var(--accent-ink)" : delta < 0 ? "var(--warn)" : "var(--ink-3)";

  return (
    <div className="panel" style={{ marginTop: 12, padding: 14, borderStyle: "dashed" }}>
      <div className="row between wrap" style={{ gap: 8, marginBottom: 10 }}>
        <span className="kicker" style={{ fontSize: 9.5 }}>Construction fee basis</span>
        <span className="badge" style={{ fontSize: 9 }}><Icon.gear width={10} />Studio only · not shown to the client</span>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <BasisTab on={!fixedOn} onClick={() => set(undefined)} label="% of fee" sub={`${pctShare}% · ${money(est.constructionPctAmount, ccy)}`} />
        <BasisTab on={fixedOn} onClick={() => set(est.constructionPctAmount)} label="Fixed fee" sub={fixedOn ? money(est.constructionAmount, ccy) : "set an amount"} />
      </div>

      {fixedOn && (
        <>
          <div className="row" style={{ gap: 8, alignItems: "center", margin: "12px 0" }}>
            <span className="dim mono" style={{ fontSize: 13 }}>{ccy}</span>
            <input className="input mono" type="number" min={0} step={500} value={config.constructionFixed ?? 0}
              onChange={(e) => set(Math.max(0, Number(e.target.value)))} style={{ maxWidth: 170, textAlign: "right" }} />
            <button className="chip" style={{ fontSize: 11.5 }} onClick={() => set(est.constructionPctAmount)}><Icon.swap width={12} />Match % basis</button>
          </div>
          <div className="basis-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            <BasisCell label={`% basis · ${pctShare}% of fee`} value={money(est.constructionPctAmount, ccy)} />
            <BasisCell label="Fixed fee" value={money(est.constructionAmount, ccy)} />
            <BasisCell label="Difference" value={`${delta > 0 ? "+" : delta < 0 ? "−" : ""}${money(Math.abs(delta), ccy)}`} color={deltaColor} />
          </div>
          <div className="row between wrap" style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--line)", fontSize: 12.5, gap: 8 }}>
            <span className="dim">Total fee — would be {money(pctBasisTotal, ccy)} on the % basis</span>
            <span className="mono" style={{ fontWeight: 600 }}>{money(est.total, ccy)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function BasisTab({ on, onClick, label, sub }: { on: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button onClick={onClick} className="panel" style={{
      flex: 1, padding: "9px 12px", cursor: "pointer", textAlign: "left",
      borderColor: on ? "var(--accent)" : "var(--line)", background: on ? "var(--accent-soft)" : "var(--card)",
    }}>
      <div className="row" style={{ gap: 7 }}>
        <span style={{ width: 14, height: 14, borderRadius: 5, flexShrink: 0, display: "grid", placeItems: "center", background: on ? "var(--accent)" : "transparent", border: "1.5px solid " + (on ? "var(--accent)" : "var(--line-2)"), color: "#fff" }}>
          {on && <Icon.check width={9} />}
        </span>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
      </div>
      <div className="dim mono" style={{ fontSize: 11, marginTop: 3, paddingLeft: 21 }}>{sub}</div>
    </button>
  );
}

function BasisCell({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="panel" style={{ padding: "9px 11px", background: "var(--card)" }}>
      <div className="dim" style={{ fontSize: 10.5 }}>{label}</div>
      <div className="mono" style={{ fontSize: 14, marginTop: 3, fontWeight: 600, color: color || "var(--ink)" }}>{value}</div>
    </div>
  );
}
